import { Component, HostListener, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { StorageService } from '../../utils/services/storage.service';
import { OrderedService } from '../../utils/services/ordered.service';
import { ToastService } from '../../utils/components/toast/toast.service';
import { TutorContextService } from '../../utils/services/tutor-context.service';

@Component({
    selector: 'section',
    templateUrl: './section.component.html',
})
export class SectionComponent implements OnDestroy {
    section: any;
    sectionSubscription: Subscription;

    private destroy$ = new Subject<void>();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private storageService: StorageService,
        private orderedService: OrderedService,
        private toastService: ToastService,
        private tutorContextService: TutorContextService,
    ) { }

    ngOnInit() {
        this.sectionSubscription = this.route.paramMap.subscribe(() => {
            this.section = this.route.snapshot.data?.section;
            this.sendTutorContext();
        });

        // Re-enviar contexto al navegar entre actividades dentro de la misma sección
        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            if (this.section) this.sendTutorContext();
            // Mejorar los videos incrustados en descripciones tras renderizar el contenido.
            setTimeout(() => this.enhanceDescriptionVideos(), 250);
        });

        // Primera carga
        setTimeout(() => this.enhanceDescriptionVideos(), 300);

        if (this.storageService.get('course')?.isOrdered && !this.orderedService?.isSectionExist(this.section?.id)) {
            this.toastService.push('content-locked', 'warning');
        }
    }

    private sendTutorContext(): void {
        const course = this.storageService.get('course');
        if (!course || !this.section) return;

        // Detectar tipo de la actividad activa desde el child route
        // childUrlPath es undefined en la presentación del capítulo (ruta vacía)
        const childSnapshot = this.route.firstChild?.snapshot;
        const childUrlPath = childSnapshot?.url[0]?.path;
        const activeContentType = childUrlPath
            || this.section.sectionContents?.[0]?.context
            || 'content';

        // Solo populear actividad si hay una ruta de actividad concreta
        // En presentación de capítulo, activityId queda undefined para que la key cambie al navegar
        const activeContent = childUrlPath
            ? (this.section.sectionContents?.find((c: any) => c.context === childUrlPath)
                || this.section.sectionContents?.[0])
            : undefined;

        this.tutorContextService.sendTutorContext({
            courseId: course.id,
            courseTitle: course.title,
            courseDescription: course.description || course.longDescription || course.title || '',
            sectionId: this.section.id,
            sectionTitle: this.section.title,
            sectionDescription: this.section.description || '',
            contentType: activeContentType,
            activityId: activeContent?.id,
            activityTitle: activeContent?.title || activeContent?.name,
            activityDescription: activeContent?.description || '',
        });
    }

    /**
     * Los videos (YouTube/Vimeo/etc.) que el autor incrusta dentro del texto de las
     * descripciones (editor Froala: span.fr-video > iframe) llegan como iframes sueltos,
     * con alturas rotas y sin forma de expandir (su botón nativo es cross-origin). Aquí,
     * de forma global para cualquier actividad, los ajustamos a 16:9 y les inyectamos un
     * botón propio de pantalla completa (maximiza por CSS). El alto también se corrige por
     * CSS (.fr-video), esto añade el botón y el modo maximizado.
     */
    private enhanceDescriptionVideos(): void {
        // 1) Videos incrustados como iframe directo (Froala .fr-video o suelto en .multiline)
        const iframes = Array.from(
            document.querySelectorAll('.multiline iframe, .fr-video iframe')
        ) as HTMLIFrameElement[];

        iframes.forEach((iframe) => {
            const src = iframe.getAttribute('src') || '';
            if (!/youtube\.com|youtu\.be|player\.vimeo\.com|vimeo\.com|dailymotion\.com/.test(src)) { return; }
            if (iframe.getAttribute('data-venh') === '1') { return; }
            iframe.setAttribute('data-venh', '1');

            // Determinar el contenedor (shell): el wrapper de Froala .fr-video si existe,
            // si no, envolvemos el iframe en un div propio.
            let shell: HTMLElement;
            const parent = iframe.parentElement;
            if (parent && parent.classList.contains('fr-video')) {
                shell = parent;
            } else {
                shell = document.createElement('div');
                iframe.parentNode.insertBefore(shell, iframe);
                shell.appendChild(iframe);
            }
            shell.classList.add('desc-video-shell');

            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.setAttribute('allowfullscreen', 'true');

            this.addExpandButton(shell);
        });

        // 2) Videos incrustados vía Embedly (.fr-embedly): el iframe interno no tiene src
        //    de YouTube (lo carga Embedly), así que tratamos el propio .fr-embedly como shell.
        const embedlies = Array.from(document.querySelectorAll('.fr-embedly')) as HTMLElement[];
        embedlies.forEach((el) => {
            if (el.getAttribute('data-venh') === '1') { return; }
            el.setAttribute('data-venh', '1');
            el.style.removeProperty('height'); // quita el height fijo inline (p. ej. 482px)
            el.classList.add('desc-video-shell');
            this.addExpandButton(el);
        });
    }

    /** Inyecta el botón (solo icono) de expandir/contraer sobre un contenedor de video. */
    private addExpandButton(shell: HTMLElement): void {
        const expandSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3"></path><path d="M21 8V5a2 2 0 0 0-2-2h-3"></path><path d="M3 16v3a2 2 0 0 0 2 2h3"></path><path d="M16 21h3a2 2 0 0 0 2-2v-3"></path></svg>';
        const collapseSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14h3a2 2 0 0 1 2 2v3"></path><path d="M20 10h-3a2 2 0 0 1-2-2V5"></path><path d="M14 20v-3a2 2 0 0 1 2-2h3"></path><path d="M10 4v3a2 2 0 0 1-2 2H5"></path></svg>';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'desc-video-expand';
        btn.title = 'Ver pantalla completa';
        btn.innerHTML = expandSvg;
        btn.addEventListener('click', (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            const max = shell.classList.toggle('desc-video-shell--max');
            btn.classList.toggle('desc-video-expand--max', max);
            btn.title = max ? 'Cerrar' : 'Ver pantalla completa';
            // Expandido: solo icono. Maximizado: icono + palabra "Cerrar".
            btn.innerHTML = max ? (collapseSvg + '<span>Cerrar</span>') : expandSvg;
            // Fondo blanco de pantalla completa detrás del video mientras está maximizado.
            document.body.classList.toggle('desc-video-maximized', !!document.querySelector('.desc-video-shell--max'));
        });
        shell.appendChild(btn);
    }

    /** Salir del modo maximizado de un video de descripción con Escape. */
    @HostListener('document:keydown.escape')
    onEscape(): void {
        document.querySelectorAll('.desc-video-shell--max').forEach((el) => {
            el.classList.remove('desc-video-shell--max');
            const btn = el.querySelector('.desc-video-expand');
            if (btn) {
                btn.classList.remove('desc-video-expand--max');
                (btn as HTMLElement).title = 'Ver pantalla completa';
            }
        });
        document.body.classList.remove('desc-video-maximized');
    }

    ngOnDestroy(): void {
        this.sectionSubscription.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
